import { cn } from './cn.js';

import type { CSSProperties, ReactElement } from 'react';

interface GlassAvatarProps {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  status?: 'online' | 'offline' | 'busy';
}

const sizeClasses = {
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
} as const;

const statusDotSize = {
  sm: 'w-1.5 h-1.5 border',
  md: 'w-2 h-2 border-[1.5px]',
  lg: 'w-2.5 h-2.5 border-2',
} as const;

const statusColors = {
  online: 'bg-success',
  offline: 'bg-text-tertiary',
  busy: 'bg-danger',
} as const;

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const avatarGradient: CSSProperties = {
  background: 'linear-gradient(135deg, #3178b7 0%, #06b6d4 100%)',
};

export function GlassAvatar({
  name,
  size = 'md',
  className,
  status,
}: GlassAvatarProps): ReactElement {
  return (
    <div className={cn('relative inline-flex', className)}>
      <div
        className={cn(
          'inline-flex items-center justify-center rounded-full',
          'text-white font-semibold',
          'ring-2 ring-white/30',
          sizeClasses[size],
        )}
        style={avatarGradient}
        title={name}
      >
        {getInitials(name)}
      </div>
      {status !== undefined && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-white',
            statusDotSize[size],
            statusColors[status],
          )}
        />
      )}
    </div>
  );
}
